/**
 * The task area: the widget the domain pack chose for this screen, or an overlay a reply asked for.
 * Props are checked against the widget's schema first; anything wrong is shown, never a blank area.
 */
import { useEffect, useMemo } from 'react';
import { patchDraft, type UiState } from '../../core/ui-state';
import type { WidgetCall } from '../../packs/contract';
import { useApp } from '../../react/app';
import { useAppearance, useDevMode, useMode, useScreen, useTranslate, useUi } from '../../react/hooks';
import { ErrorBoundary } from '../../shell/ErrorBoundary';
import { Icon } from '../../ui-kit/Icon';
import { CloseContext, Problem } from '../../ui-kit/Panel';
import { pickVariant, type WidgetApi } from '../../widgets/contract';
import { AttractVideo } from '../../widgets/generic/Attract';
import css from './Content.module.css';
import { OfferPill } from './OfferPill';

export function ContentModule() {
  const screen = useScreen();
  const overlay = useUi((u) => u.overlay);
  const t = useTranslate();
  const dev = useDevMode();
  const app = useApp();

  if (!screen.ctx || !screen.def) {
    // no session yet (a replay at its first step; live, the boot screen covers this): the waiting video
    const attract = app.pack.attract;
    return attract ? <div className={css.content}><div className={css.stage}><AttractVideo src={attract.video} trimEndS={attract.trimEndS} /></div></div> : null;
  }

  if (overlay) return <Overlay overlay={overlay} />;

  const call = screen.def.view(screen.ctx);
  return (
    <div className={css.content}>
      <OfferPill />
      <div className={css.stage} key={screen.name ?? 'none'}>
        {!screen.known && dev && <Problem title={t('screen.unknown', { name: screen.name ?? '?' })} />}
        {call && <WidgetHost call={call} resetKey={screen.name} />}
      </div>
    </div>
  );
}

/**
 * An overlay a reply opened (the station map), with its close button: in the card's own header when
 * it has one (CloseContext), else floating at the corner. It closes by itself `closeAfterMs` after the
 * reply or the last touch on it, as the old kiosk's map.
 */
function Overlay({ overlay }: { overlay: NonNullable<UiState['overlay']> }) {
  const { ui } = useApp();
  const t = useTranslate();
  const close = () => ui.set((s) => ({ ...s, overlay: null }));
  const touched = () => ui.set((s) => (s.overlay?.closeAfterMs ? { ...s, overlay: { ...s.overlay, closeAt: Date.now() + s.overlay.closeAfterMs } } : s));

  useEffect(() => {
    if (overlay.closeAt === null) return;
    const timer = setTimeout(() => ui.set((s) => (s.overlay?.closeAt === overlay.closeAt ? { ...s, overlay: null } : s)), Math.max(0, overlay.closeAt - Date.now()));
    return () => clearTimeout(timer);
  }, [overlay.closeAt, ui]);

  return (
    <div className={`${css.content} ${css.withClose}`} onPointerDownCapture={touched} onWheelCapture={touched}>
      <button className={css.close} onClick={close} aria-label={t('overlay.close')}>
        <Icon name="close" size={40} />
      </button>
      <div className={css.stage} key={`overlay-${overlay.turnId}`}>
        <CloseContext value={{ close, label: t('overlay.close') }}>
          <WidgetHost call={overlay} resetKey={overlay.turnId} />
        </CloseContext>
      </div>
    </div>
  );
}

function WidgetHost({ call, resetKey }: { call: WidgetCall; resetKey: unknown }) {
  const app = useApp();
  const screen = useScreen();
  const mode = useMode();
  const t = useTranslate();
  const { screenVariants, widgetVariants, preferredVariants } = useAppearance();
  const def = app.widgets.get(call.widget);

  const api = useMemo<WidgetApi>(() => ({
    press: (button, data = {}) => { app.voice?.unlockAudio(); void app.kiosk.press(button, data); },
    say: (text) => { app.voice?.unlockAudio(); void app.kiosk.say(text); },
    setDraft: (patch) => patchDraft(app.ui, patch),
    readOnly: app.kiosk.role === 'replay',
    swipe: mode.behaviour.swipe,
  }), [app, mode.behaviour.swipe]);

  if (!def) return <Problem title={t('widget.unknown', { name: call.widget })} />;
  const parsed = def.schema.safeParse(call.props);
  if (!parsed.success) {
    return (
      <Problem
        title={t('widget.bad_props', { name: call.widget })}
        detail={parsed.error.issues.slice(0, 6).map((i) => `${i.path.join('.')}: ${i.message}`).join('\n')}
        detailLabel={t('error.details')}
      />
    );
  }
  const chosenForScreen = screen.name ? screenVariants[screen.name] : undefined;
  const variant = pickVariant(def, [chosenForScreen, widgetVariants[call.widget], ...preferredVariants, call.variant]);
  return (
    <ErrorBoundary name={`widget ${call.widget}`} resetKey={resetKey}>
      {variant.edgeToEdge
        ? <div className={css.edgeToEdge}><variant.Component props={parsed.data as never} ctx={screen.ctx!} api={api} /></div>
        : <variant.Component props={parsed.data as never} ctx={screen.ctx!} api={api} />}
    </ErrorBoundary>
  );
}
